import { inject, injectable } from 'inversify';

import type {
  CreateProjectPayload,
  Page,
  ProjectBody,
  ProjectData,
  ProjectFont,
  ProjectPayload,
  PublicProjectBody,
  PublicProjectFont,
  UpdateProjectPayload,
} from '../models/project.model';
import { TYPES } from '../projects.types';
import { ProjectsService } from '../services/projects.service';

@injectable()
export class ProjectsRepository {
  public constructor(@inject(TYPES.ProjectsService) private readonly service: ProjectsService) {}

  public async fetchProject(
    id: string | number,
    type: ProjectPayload['type']
  ): Promise<ProjectBody> {
    const response = await this.service.fetchProjectData({
      id,
      type,
    });

    if (!response || !response.data) {
      throw new Error(`Project with ID ${id} not found`);
    }

    return response.data as ProjectBody;
  }

  public async fetchPublicProject(
    id: string | number,
    type: ProjectPayload['type']
  ): Promise<PublicProjectBody> {
    const response = await this.service.fetchPublicProjectData({
      id,
      type,
    });

    if (!response || !response.data.id) {
      throw new Error(`Public project with ID ${id} not found`);
    }

    return response.data;
  }

  public async fetchPage(projectId: string | number, pageId: string | number): Promise<Page> {
    const response = await this.service.fetchPage(projectId, pageId);

    if (!response || !response.data) {
      throw new Error(`Page with ID ${pageId} not found`);
    }

    return response.data as Page;
  }

  public async fetchPublicPage(projectId: string | number, pageId: string | number): Promise<Page> {
    const response = await this.service.fetchPublicPage(projectId, pageId);

    if (!response || !response.data) {
      throw new Error(`Public page with ID ${pageId} not found`);
    }

    return response.data as Page;
  }

  public async fetchFontData(fontId: number): Promise<ProjectFont> {
    const response = await this.service.fetchFontData(fontId);

    return response.data as ProjectFont;
  }

  public async fetchPublicProjectFonts(projectId: string | number): Promise<PublicProjectFont> {
    const response = await this.service.fetchPublicProjectFonts(projectId);

    return response.data as PublicProjectFont;
  }

  public async createProject(payload: CreateProjectPayload): Promise<ProjectData> {
    const response = await this.service.createProject(payload);

    if (!response || !response.data) {
      throw new Error('Failed to create project');
    }

    return response.data as ProjectData;
  }

  public async updateProject(
    data: UpdateProjectPayload,
    existingProject?: ProjectBody
  ): Promise<ProjectData> {
    const response = await this.service.updateProject(data);

    if (!response || !response.data) {
      throw new Error(`Failed to update project with ID ${data.id}`);
    }

    const updatedProject = response.data as ProjectData;

    // Merge existing page dimensions if existingProject is provided
    if (existingProject) {
      const mergedProject = {
        ...updatedProject,
        pages: updatedProject.pages.map((updatedPage, index) => ({
          ...updatedPage,
          width: updatedPage.width ?? existingProject.pages[index]?.width,
          height: updatedPage.height ?? existingProject.pages[index]?.height,
        })),
      } as ProjectData;

      return mergedProject;
    }

    return updatedProject;
  }
}
